import { useEffect, useMemo, useRef, useState } from "react";
import { api, HttpError } from "../api/client";
import type { ScheduleEvent, ScheduleEventInput, ScheduleKind, TeamMember } from "../api/types";

/**
 * Create or edit an Event.
 *
 * <p>The kind is a free-text input with a datalist rather than a select. A
 * fixed vocabulary would need a migration every time the team invents a kind of
 * day, and a bare text box would give us "Arbetsdag", "arbetsdag" and
 * "Arbetsdagar" within a season. Suggesting what is already in use makes the
 * consistent choice the lazy one without ever refusing a new kind.
 *
 * <p>The invitee picker starts with everyone ticked, which is both the common
 * case and the behaviour every Event used to have. Leaving someone off is
 * therefore a deliberate act — the failure mode this feature introduces is
 * "Erik never heard about Saturday", and defaults are the only real defence
 * against it.
 */

interface Props {
  /** The Event being edited, or null when creating. */
  event: ScheduleEvent | null;
  kinds: ScheduleKind[];
  onSaved: (saved: ScheduleEvent) => void;
  onClose: () => void;
}

export function EventForm({ event, kinds, onSaved, onClose }: Props) {
  const [date, setDate] = useState(event?.date ?? todayISO());
  // <input type="time"> wants HH:mm; the API sends HH:mm:ss.
  const [startTime, setStartTime] = useState((event?.startTime ?? "").slice(0, 5));
  const [kind, setKind] = useState(event?.kind ?? "");
  const [title, setTitle] = useState(event?.title ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [locationText, setLocationText] = useState(event?.locationText ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [team, setTeam] = useState<TeamMember[]>([]);
  // Null until the roster arrives. Distinct from the empty set, which is a
  // person having unticked everybody and is refused.
  const [invited, setInvited] = useState<Set<string> | null>(null);
  // The roster failing is not a reason to block someone from arranging a day.
  // The form falls back to inviting the whole team — the same thing the ticked
  // boxes would have done, and what the server does for a null invitee list.
  const [teamFailed, setTeamFailed] = useState(false);

  const firstField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const members = await api.listTeamMembers();
        if (!live) return;
        setTeam(members);
        // Editing shows who is actually on this Event; creating starts with
        // everyone. An invitee who has since left the team is not in `members`
        // and drops out here, which is the same thing that has already
        // happened to them everywhere else on the Schedule.
        setInvited(
          event
            ? new Set(event.invitees.map((i) => i.userId))
            : new Set(members.map((m) => m.userId)),
        );
      } catch {
        if (live) setTeamFailed(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [event]);

  const allInvited = useMemo(
    () => invited !== null && team.length > 0 && team.every((m) => invited.has(m.userId)),
    [invited, team],
  );

  function toggle(userId: string) {
    setInvited((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (invited !== null && invited.size === 0) {
      setError("Invite at least one person — an event nobody is asked to has nobody to come to it.");
      return;
    }
    setSaving(true);
    setError(null);
    const body: ScheduleEventInput = {
      date,
      startTime: startTime ? `${startTime}:00` : null,
      kind: kind.trim(),
      title: title.trim(),
      description: description.trim() || null,
      locationText: locationText.trim() || null,
      // Null means the whole team. Sent when the roster never loaded, and on
      // the brief moment before it does — both are "nobody has expressed a
      // preference", which is exactly what the default is for.
      inviteeIds: invited === null ? null : [...invited],
    };
    try {
      const saved = event
        ? await api.updateEvent(event.eventId, body)
        : await api.createEvent(body);
      onSaved(saved);
    } catch (err) {
      setError(
        err instanceof HttpError && err.status === 403
          ? "Only the person who created this event, or an admin, can change it."
          : "Could not save the event. Check the fields and try again.",
      );
      setSaving(false);
    }
  }

  return (
    <div className="ev-overlay" role="presentation" onClick={onClose}>
      <div
        className="ev-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={event ? "Edit event" : "New event"}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="ev-dialog-hd">
            <h2 className="serif">{event ? "Edit event" : "New event"}</h2>
            <button type="button" className="ev-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="ev-grid">
            <label className="ev-field">
              <span>Date</span>
              <input
                ref={firstField}
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>

            <label className="ev-field">
              {/* Optional: plenty of work days have no gathering time, and a
                  placeholder 00:00 would read as midnight to everyone. */}
              <span>Gathering time</span>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder="optional"
              />
            </label>

            <label className="ev-field">
              <span>Kind</span>
              <input
                type="text"
                required
                maxLength={64}
                list="schedule-kinds"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                placeholder="Hunt day"
              />
              <datalist id="schedule-kinds">
                {kinds.map((k) => (
                  <option key={k.kind} value={k.kind} />
                ))}
              </datalist>
            </label>

            <label className="ev-field">
              <span>Where</span>
              <input
                type="text"
                maxLength={200}
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder="optional"
              />
            </label>

            <label className="ev-field ev-wide">
              <span>Title</span>
              <input
                type="text"
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Älgjakt — first drive"
              />
            </label>

            <label className="ev-field ev-wide">
              <span>Details</span>
              <textarea
                rows={4}
                maxLength={4000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="optional"
              />
            </label>

            {/* A fieldset, not a label: this is a group of controls with one
                heading over it, and wrapping checkboxes in a <label> would
                make the heading click the first one. */}
            <fieldset className="ev-field ev-wide ev-invitees">
              <legend>
                <span>Who's invited</span>
                {invited !== null && (
                  <span className="ev-invitee-count">
                    {allInvited
                      ? "everyone"
                      : `${invited.size} of ${team.length}`}
                  </span>
                )}
              </legend>

              {teamFailed ? (
                <p className="ev-invitee-note">
                  Could not load the team just now — everyone will be invited.
                </p>
              ) : invited === null ? (
                <p className="ev-invitee-note">Loading the team…</p>
              ) : (
                <>
                  <div className="ev-invitee-bulk">
                    <button
                      type="button"
                      className="sched-link"
                      onClick={() => setInvited(new Set(team.map((m) => m.userId)))}
                    >
                      Everyone
                    </button>
                    <button
                      type="button"
                      className="sched-link"
                      onClick={() => setInvited(new Set())}
                    >
                      Nobody
                    </button>
                  </div>

                  <ul className="ev-invitee-list">
                    {team.map((m) => (
                      <li key={m.userId}>
                        <label className="ev-invitee">
                          <input
                            type="checkbox"
                            checked={invited.has(m.userId)}
                            onChange={() => toggle(m.userId)}
                          />
                          <span>{m.name}</span>
                        </label>
                      </li>
                    ))}
                  </ul>

                  {/* Said plainly rather than left to be discovered. Under the
                      old Schedule every Event reached everyone, so "why didn't
                      I see it?" is a question people will now actually have. */}
                  <p className="ev-invitee-note">
                    Only the people ticked here see this event, and only they can answer.
                    {event ? " Anyone added is emailed." : " Everyone ticked is emailed."}
                  </p>
                </>
              )}
            </fieldset>
          </div>

          {error && <p className="ev-error">{error}</p>}

          <div className="ev-actions">
            <button type="button" className="chip" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="chip active"
              disabled={saving || invited?.size === 0}
            >
              {saving ? "Saving…" : event ? "Save changes" : "Create event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Local calendar parts — an Event's date is a day, never an instant. */
function todayISO(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}
